import React, { forwardRef } from 'react';
import Button, { type IButtonProps } from '@components/ui/Button/Button';
import styles from './ErrorState.module.css';

export interface IErrorStateProps extends Omit<React.HTMLAttributes<HTMLElement>, 'title'> {
  message: React.ReactNode;
  /** Rendered in an h2 above the message. */
  title?: React.ReactNode;
  titleClassName?: string;
  /** Rendered as given, before everything else. */
  icon?: React.ReactNode;
  /** Use p for an inline error inside running content; the message then needs messageAs="span". */
  as?: 'div' | 'p';
  messageAs?: 'p' | 'div' | 'span';
  messageClassName?: string;
  stacked?: boolean;
  /** Without it no button renders. Also used for actions that navigate instead of retrying. */
  onRetry?: () => void;
  retryLabel?: React.ReactNode;
  retryButtonProps?: Omit<IButtonProps, 'onClick' | 'children'>;
}

const ErrorState = forwardRef<HTMLElement, IErrorStateProps>(
  (
    {
      message,
      title,
      titleClassName,
      icon,
      as = 'div',
      messageAs = 'p',
      messageClassName,
      stacked = false,
      onRetry,
      retryLabel = 'Retry',
      retryButtonProps,
      className,
      ...rest
    },
    ref,
  ) => {
    const Root = as as 'div';
    const Message = messageAs as 'p';
    const classes = [stacked && styles.stacked, className].filter(Boolean).join(' ');

    return (
      <Root ref={ref as React.Ref<HTMLDivElement>} className={classes || undefined} {...rest}>
        {icon}
        {title && <h2 className={titleClassName}>{title}</h2>}
        <Message className={messageClassName}>{message}</Message>
        {onRetry && (
          <Button {...retryButtonProps} onClick={() => onRetry()}>
            {retryLabel}
          </Button>
        )}
      </Root>
    );
  },
);

ErrorState.displayName = 'ErrorState';

export default ErrorState;
